/**
 * The sidebar sections for the simulation profile, the values that pre-fill every mocked
 * tool result in a run. Each section idles as a summary of the fields that are set and opens
 * into its form on click; a form leads with its essentials, the rarer fields behind
 * "+ group" pills.
 */

import { useState, type ReactNode } from 'react';
import { Plus, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Field, FieldInput, FieldSelect, RevealGroup } from '../ui/field';
import { PillButton } from '../ui/pill-button';
import { Segmented } from '../ui/segmented';
import { EditableSummary, SummaryTag, type SummaryEntry } from '../ui/summary';
import { humanize } from '../ui/text';
import { CustomerDetailsForm, type CustomerDetails } from './customer-details';
import type { OrderItem, ProductVariant, SimulationProfileExtras } from './simulation-profile.store';
import {
  ORDER_STATUS_OPTIONS,
  PAYMENT_STATUS_OPTIONS,
  SUBSCRIPTION_STATUS_OPTIONS,
  SUBSCRIPTION_FREQUENCY_OPTIONS,
} from './simulation-profile.store';

const TOOL_DEFAULT = 'Tool default';

const isSet = (v: string | undefined): v is string => Boolean(v && v.trim());
const anySet = (...values: string[]): boolean => values.some(isSet);

const NOT_FOUND: SummaryEntry = { label: 'Record', value: <SummaryTag text='not found' /> };

const RECORD_OPTIONS = [
  { value: 'exists', label: 'Exists' },
  { value: 'false', label: 'Not found' },
];

/**
 * Whether this record class exists in the scenario's world. 'false' makes every
 * tool that reads it return its real not-found; anything else keeps the fixture.
 */
const RecordToggle = ({ value, onChange }: { value: string; onChange: (value: string) => void }) => (
  <div className='flex items-center justify-between gap-3'>
    <span className='text-xs leading-4 text-gray-800'>Record</span>
    <Segmented
      options={RECORD_OPTIONS}
      value={value === 'false' ? 'false' : 'exists'}
      onChange={(v) => onChange(v === 'false' ? 'false' : '')}
    />
  </div>
);

const Note = ({ children }: { children: ReactNode }) => <p className='text-xs leading-4 text-gray-800'>{children}</p>;

const Grid = ({ children }: { children: ReactNode }) => <div className='grid grid-cols-2 gap-2.5'>{children}</div>;

/** Holds RevealGroups: open ones lead, closed ones follow side by side. */
const Reveals = ({ children }: { children: ReactNode }) => <div className='flex flex-wrap gap-2'>{children}</div>;

const EditForm = ({ onDone, children }: { onDone: () => void; children: ReactNode }) => (
  <div className='flex flex-col gap-3'>
    {children}
    <div className='flex justify-end'>
      <PillButton variant='primary' onClick={onDone}>
        Done
      </PillButton>
    </div>
  </div>
);

const RemoveRowButton = ({ label, onClick }: { label: string; onClick: () => void }) => (
  <Button
    variant='ghost'
    size='icon'
    className='size-8 shrink-0 rounded-lg text-gray-800 hover:bg-gray-100 hover:text-destructive'
    aria-label={label}
    onClick={onClick}
  >
    <X className='h-3.5 w-3.5' />
  </Button>
);

// ── Customer ──────────────────────────────────────────────────────────────────

interface CustomerSectionProps {
  customer: CustomerDetails;
  onCustomerChange: (value: CustomerDetails) => void;
  extras: SimulationProfileExtras;
  onExtrasChange: (value: SimulationProfileExtras) => void;
}

const customerEntries = (customer: CustomerDetails, extras: SimulationProfileExtras): SummaryEntry[] => {
  const entries: SummaryEntry[] = [];
  // The writer is still a person with a name and email; only the support-system
  // record is missing, so the flag joins the list instead of replacing it.
  if (extras.customer_exists === 'false') entries.push(NOT_FOUND);
  if (isSet(customer.name)) entries.push({ label: 'Name', value: customer.name });
  if (isSet(customer.email)) entries.push({ label: 'Email', value: customer.email });
  if (isSet(extras.customer_phone)) entries.push({ label: 'Phone', value: extras.customer_phone });
  if (isSet(extras.customer_address)) entries.push({ label: 'Address', value: extras.customer_address });
  return entries;
};

export const CustomerProfileSection = ({ customer, onCustomerChange, extras, onExtrasChange }: CustomerSectionProps) => {
  const [editing, setEditing] = useState(false);
  if (!editing) {
    return (
      <EditableSummary entries={customerEntries(customer, extras)} addLabel='Add customer' onEdit={() => setEditing(true)} />
    );
  }
  return (
    <EditForm onDone={() => setEditing(false)}>
      <RecordToggle
        value={extras.customer_exists}
        onChange={(customer_exists) => onExtrasChange({ ...extras, customer_exists })}
      />
      {extras.customer_exists === 'false' && (
        <Note>Lookups fail and searches come back empty. Name and email still describe who is writing in.</Note>
      )}
      <CustomerDetailsForm value={customer} onChange={onCustomerChange} />
      <Reveals>
        <RevealGroup label='Contact' filled={anySet(extras.customer_phone, extras.customer_address)}>
          <Grid>
            <Field label='Phone'>
              <FieldInput
                value={extras.customer_phone}
                onChange={(e) => onExtrasChange({ ...extras, customer_phone: e.target.value })}
                placeholder='+1 555 010 0123'
              />
            </Field>
            <Field label='Address'>
              <FieldInput
                value={extras.customer_address}
                onChange={(e) => onExtrasChange({ ...extras, customer_address: e.target.value })}
                placeholder='221B Baker St'
              />
            </Field>
          </Grid>
        </RevealGroup>
      </Reveals>
    </EditForm>
  );
};

// ── Order ─────────────────────────────────────────────────────────────────────

interface ProfileSectionProps {
  value: SimulationProfileExtras;
  onChange: (value: SimulationProfileExtras) => void;
}

/** One line's worth of the summary: "2 × Street Skate Deck · 35.00". */
const describeItem = (item: OrderItem): string =>
  [isSet(item.quantity) ? `${item.quantity} ×` : null, item.title.trim(), isSet(item.price) ? `· ${item.price}` : null]
    .filter(Boolean)
    .join(' ');

const EMPTY_ITEM: OrderItem = { title: '', quantity: '', price: '' };

/**
 * Repeatable line items. An order can hold several, and some flows turn on that
 * (split shipments, partial refunds), so this is a list rather than one fixed row.
 */
const OrderItemRows = ({ items, onChange }: { items: OrderItem[]; onChange: (items: OrderItem[]) => void }) => {
  const update = (index: number, patch: Partial<OrderItem>) =>
    onChange(items.map((item, i) => (i === index ? { ...item, ...patch } : item)));

  return (
    <div className='flex flex-col gap-2'>
      {items.map((item, index) => (
        <div key={index} className='grid grid-cols-[1fr_3rem_4.5rem_auto] items-end gap-1.5'>
          <FieldInput value={item.title} onChange={(e) => update(index, { title: e.target.value })} placeholder='Item' />
          <FieldInput
            value={item.quantity}
            onChange={(e) => update(index, { quantity: e.target.value })}
            placeholder='Qty'
            inputMode='numeric'
          />
          <FieldInput value={item.price} onChange={(e) => update(index, { price: e.target.value })} placeholder='Price' />
          <RemoveRowButton
            label={`Remove item ${index + 1}`}
            onClick={() => onChange(items.filter((_, i) => i !== index))}
          />
        </div>
      ))}
      <PillButton dashed className='w-fit' onClick={() => onChange([...items, { ...EMPTY_ITEM }])}>
        <Plus className='h-3.5 w-3.5' /> Add item
      </PillButton>
    </div>
  );
};

const orderEntries = (value: SimulationProfileExtras): SummaryEntry[] => {
  if (value.order_exists === 'false') return [NOT_FOUND];
  const entries: SummaryEntry[] = [];
  if (isSet(value.order_number))
    entries.push({ label: 'Order', value: <span className='font-mono'>{value.order_number}</span> });
  if (isSet(value.order_amount)) entries.push({ label: 'Amount', value: value.order_amount });
  if (isSet(value.order_status)) entries.push({ label: 'Fulfillment', value: <SummaryTag text={value.order_status} /> });
  if (isSet(value.order_payment_status))
    entries.push({ label: 'Payment', value: <SummaryTag text={value.order_payment_status} /> });
  if (isSet(value.order_date)) entries.push({ label: 'Ordered', value: value.order_date });
  if (isSet(value.order_delivery_date)) entries.push({ label: 'Delivery', value: value.order_delivery_date });
  if (isSet(value.order_tracking_number)) {
    entries.push({
      label: 'Tracking',
      value: `${value.order_tracking_number}${isSet(value.order_carrier) ? ` · ${value.order_carrier}` : ''}`,
    });
  } else if (isSet(value.order_carrier)) {
    entries.push({ label: 'Carrier', value: value.order_carrier });
  }
  if (isSet(value.order_tracking_status))
    entries.push({ label: 'Shipment', value: <SummaryTag text={value.order_tracking_status} /> });
  if (isSet(value.order_tracking_url)) {
    entries.push({
      label: 'Tracking URL',
      value: (
        <span className='block truncate' title={value.order_tracking_url}>
          {value.order_tracking_url}
        </span>
      ),
    });
  }
  const items = value.order_items.filter((i) => isSet(i.title));
  if (items.length > 0) {
    entries.push({
      label: items.length > 1 ? 'Items' : 'Item',
      value: (
        <span className='flex flex-col gap-0.5'>
          {items.map((item, i) => (
            <span key={`${item.title}-${i}`}>{describeItem(item)}</span>
          ))}
        </span>
      ),
    });
  }
  return entries;
};

export const OrderProfileSection = ({ value, onChange }: ProfileSectionProps) => {
  const [editing, setEditing] = useState(false);
  if (!editing) {
    return <EditableSummary entries={orderEntries(value)} addLabel='Add order' onEdit={() => setEditing(true)} />;
  }
  const recordToggle = (
    <RecordToggle value={value.order_exists} onChange={(order_exists) => onChange({ ...value, order_exists })} />
  );
  if (value.order_exists === 'false') {
    return (
      <EditForm onDone={() => setEditing(false)}>
        {recordToggle}
        <Note>Order lookups fail and lists come back empty. The other order fields are ignored.</Note>
      </EditForm>
    );
  }
  return (
    <EditForm onDone={() => setEditing(false)}>
      {recordToggle}
      <Grid>
        <Field label='Order number'>
          <FieldInput
            value={value.order_number}
            onChange={(e) => onChange({ ...value, order_number: e.target.value })}
            placeholder='#1186'
          />
        </Field>
        <Field label='Amount'>
          <FieldInput
            value={value.order_amount}
            onChange={(e) => onChange({ ...value, order_amount: e.target.value })}
            placeholder='70.00'
          />
        </Field>
        <Field label='Fulfillment'>
          <FieldSelect
            value={value.order_status}
            options={ORDER_STATUS_OPTIONS}
            unsetLabel={TOOL_DEFAULT}
            onChange={(order_status) => onChange({ ...value, order_status })}
          />
        </Field>
        <Field label='Payment'>
          <FieldSelect
            value={value.order_payment_status}
            options={PAYMENT_STATUS_OPTIONS}
            unsetLabel={TOOL_DEFAULT}
            onChange={(order_payment_status) => onChange({ ...value, order_payment_status })}
          />
        </Field>
      </Grid>
      <Reveals>
        <RevealGroup label='Dates' filled={anySet(value.order_date, value.order_delivery_date)}>
          <Grid>
            <Field label='Ordered'>
              <FieldInput
                type='date'
                value={value.order_date}
                onChange={(e) => onChange({ ...value, order_date: e.target.value })}
              />
            </Field>
            <Field label='Delivery'>
              <FieldInput
                type='date'
                value={value.order_delivery_date}
                onChange={(e) => onChange({ ...value, order_delivery_date: e.target.value })}
              />
            </Field>
          </Grid>
        </RevealGroup>
        <RevealGroup
          label='Shipment'
          filled={anySet(
            value.order_tracking_number,
            value.order_carrier,
            value.order_tracking_status,
            value.order_tracking_url
          )}
        >
          <Grid>
            <Field label='Tracking number'>
              <FieldInput
                value={value.order_tracking_number}
                onChange={(e) => onChange({ ...value, order_tracking_number: e.target.value })}
                placeholder='TRK-1234'
              />
            </Field>
            <Field label='Carrier'>
              <FieldInput
                value={value.order_carrier}
                onChange={(e) => onChange({ ...value, order_carrier: e.target.value })}
                placeholder='FedEx'
              />
            </Field>
            <Field label='Status'>
              <FieldInput
                value={value.order_tracking_status}
                onChange={(e) => onChange({ ...value, order_tracking_status: e.target.value })}
                placeholder='In Transit'
              />
            </Field>
            <Field label='Tracking URL'>
              <FieldInput
                value={value.order_tracking_url}
                onChange={(e) => onChange({ ...value, order_tracking_url: e.target.value })}
                placeholder='https://…'
              />
            </Field>
          </Grid>
        </RevealGroup>
        <RevealGroup label='Items' filled={value.order_items.some((i) => isSet(i.title))}>
          <OrderItemRows items={value.order_items} onChange={(order_items) => onChange({ ...value, order_items })} />
        </RevealGroup>
      </Reveals>
    </EditForm>
  );
};

// ── Subscription ──────────────────────────────────────────────────────────────

const subscriptionEntries = (value: SimulationProfileExtras): SummaryEntry[] => {
  if (value.subscription_exists === 'false') return [NOT_FOUND];
  const entries: SummaryEntry[] = [];
  if (isSet(value.subscription_product)) entries.push({ label: 'Plan', value: value.subscription_product });
  if (isSet(value.subscription_status))
    entries.push({ label: 'Status', value: <SummaryTag text={value.subscription_status} /> });
  if (isSet(value.subscription_frequency))
    entries.push({ label: 'Frequency', value: humanize(value.subscription_frequency) });
  if (isSet(value.subscription_next_date)) entries.push({ label: 'Next order', value: value.subscription_next_date });
  if (isSet(value.subscription_price)) entries.push({ label: 'Price', value: value.subscription_price });
  if (isSet(value.subscription_delivery_count))
    entries.push({ label: 'Deliveries', value: value.subscription_delivery_count });
  if (isSet(value.subscription_id))
    entries.push({ label: 'Id', value: <span className='font-mono'>{value.subscription_id}</span> });
  return entries;
};

export const SubscriptionProfileSection = ({ value, onChange }: ProfileSectionProps) => {
  const [editing, setEditing] = useState(false);
  if (!editing) {
    return (
      <EditableSummary entries={subscriptionEntries(value)} addLabel='Add subscription' onEdit={() => setEditing(true)} />
    );
  }
  const recordToggle = (
    <RecordToggle
      value={value.subscription_exists}
      onChange={(subscription_exists) => onChange({ ...value, subscription_exists })}
    />
  );
  if (value.subscription_exists === 'false') {
    return (
      <EditForm onDone={() => setEditing(false)}>
        {recordToggle}
        <Note>The account shows no subscriptions and detail fetches fail. The other fields are ignored.</Note>
      </EditForm>
    );
  }
  return (
    <EditForm onDone={() => setEditing(false)}>
      {recordToggle}
      <Field label='Plan'>
        <FieldInput
          value={value.subscription_product}
          onChange={(e) => onChange({ ...value, subscription_product: e.target.value })}
          placeholder='House Blend Coffee — 250g'
        />
      </Field>
      <Grid>
        <Field label='Status'>
          <FieldSelect
            value={value.subscription_status}
            options={SUBSCRIPTION_STATUS_OPTIONS}
            unsetLabel={TOOL_DEFAULT}
            onChange={(subscription_status) => onChange({ ...value, subscription_status })}
          />
        </Field>
        <Field label='Frequency'>
          <FieldSelect
            value={value.subscription_frequency}
            options={SUBSCRIPTION_FREQUENCY_OPTIONS}
            unsetLabel={TOOL_DEFAULT}
            onChange={(subscription_frequency) => onChange({ ...value, subscription_frequency })}
          />
        </Field>
      </Grid>
      <Reveals>
        <RevealGroup
          label='Billing'
          filled={anySet(value.subscription_next_date, value.subscription_price, value.subscription_delivery_count)}
        >
          <Grid>
            <Field label='Next order'>
              <FieldInput
                type='date'
                value={value.subscription_next_date}
                onChange={(e) => onChange({ ...value, subscription_next_date: e.target.value })}
              />
            </Field>
            <Field label='Price'>
              <FieldInput
                value={value.subscription_price}
                onChange={(e) => onChange({ ...value, subscription_price: e.target.value })}
                placeholder='35.00'
              />
            </Field>
            <Field label='Deliveries so far'>
              <FieldInput
                value={value.subscription_delivery_count}
                onChange={(e) => onChange({ ...value, subscription_delivery_count: e.target.value })}
                placeholder='3'
              />
            </Field>
          </Grid>
        </RevealGroup>
        <RevealGroup label='Subscription id' filled={isSet(value.subscription_id)}>
          <FieldInput
            value={value.subscription_id}
            onChange={(e) => onChange({ ...value, subscription_id: e.target.value })}
            placeholder='sub_9f21…'
          />
        </RevealGroup>
      </Reveals>
    </EditForm>
  );
};

// ── Product ───────────────────────────────────────────────────────────────────

const EMPTY_VARIANT: ProductVariant = { title: '', available_for_sale: true };

const AVAILABILITY_OPTIONS = [
  { value: 'true', label: 'Available' },
  { value: 'false', label: 'Unavailable' },
];

const ProductVariantRows = ({
  variants,
  onChange,
}: {
  variants: ProductVariant[];
  onChange: (variants: ProductVariant[]) => void;
}) => {
  const update = (index: number, patch: Partial<ProductVariant>) =>
    onChange(variants.map((variant, i) => (i === index ? { ...variant, ...patch } : variant)));

  return (
    <div className='flex flex-col gap-2'>
      {variants.map((variant, index) => (
        <div key={index} className='grid grid-cols-[1fr_7rem_auto] items-end gap-1.5'>
          <FieldInput
            value={variant.title}
            onChange={(e) => update(index, { title: e.target.value })}
            placeholder='Whole Bean'
          />
          <FieldSelect
            value={String(variant.available_for_sale)}
            options={AVAILABILITY_OPTIONS}
            onChange={(v) => update(index, { available_for_sale: v === 'true' })}
          />
          <RemoveRowButton
            label={`Remove variant ${index + 1}`}
            onClick={() => onChange(variants.filter((_, i) => i !== index))}
          />
        </div>
      ))}
      <PillButton dashed className='w-fit' onClick={() => onChange([...variants, { ...EMPTY_VARIANT }])}>
        <Plus className='h-3.5 w-3.5' /> Add variant
      </PillButton>
    </div>
  );
};

const productEntries = (value: SimulationProfileExtras): SummaryEntry[] => {
  const entries: SummaryEntry[] = [];
  const variants = value.product_variants.filter((v) => isSet(v.title));
  if (isSet(value.product_title)) entries.push({ label: 'Product', value: value.product_title });
  if (isSet(value.product_price)) entries.push({ label: 'Price', value: value.product_price });
  if (variants.length)
    entries.push({
      label: variants.length > 1 ? 'Variants' : 'Variant',
      value: (
        <span className='flex flex-wrap gap-1'>
          {variants.map((variant, index) => (
            <SummaryTag key={index} text={`${variant.title.trim()} · ${variant.available_for_sale ? 'yes' : 'no'}`} />
          ))}
        </span>
      ),
    });
  else if (isSet(value.product_in_stock))
    entries.push({
      label: 'Availability',
      value: <SummaryTag text={value.product_in_stock === 'true' ? 'in stock' : 'out of stock'} />,
    });
  return entries;
};

export const ProductProfileSection = ({ value, onChange }: ProfileSectionProps) => {
  const [editing, setEditing] = useState(false);
  if (!editing) {
    return <EditableSummary entries={productEntries(value)} addLabel='Add product' onEdit={() => setEditing(true)} />;
  }
  const named = value.product_variants.filter((v) => isSet(v.title));
  const hasVariants = named.length > 0;
  return (
    <EditForm onDone={() => setEditing(false)}>
      <Field label='Product'>
        <FieldInput
          value={value.product_title}
          onChange={(e) => onChange({ ...value, product_title: e.target.value })}
          placeholder='House Blend Coffee — 250g'
        />
      </Field>
      <Grid>
        <Field label='Price'>
          <FieldInput
            value={value.product_price}
            onChange={(e) => onChange({ ...value, product_price: e.target.value })}
            placeholder='35.00'
          />
        </Field>
        <Field label='Availability'>
          <FieldSelect
            value={hasVariants ? String(named.some((v) => v.available_for_sale)) : value.product_in_stock}
            options={[
              { value: 'true', label: 'In stock' },
              { value: 'false', label: 'Out of stock' },
            ]}
            unsetLabel={TOOL_DEFAULT}
            disabled={hasVariants}
            onChange={(product_in_stock) => onChange({ ...value, product_in_stock })}
          />
        </Field>
      </Grid>
      <Reveals>
        <RevealGroup label='Variants' filled={hasVariants}>
          <ProductVariantRows
            variants={value.product_variants}
            onChange={(product_variants) => onChange({ ...value, product_variants })}
          />
        </RevealGroup>
      </Reveals>
    </EditForm>
  );
};
